// Casino Dashboard › Games
//
// Ported from casino_dashboard/src/layouts/games. Period / top-list buttons
// switch between GET /dashboard/gametransaction/{days} and
// GET /dashboard/topgamelist/2/{n}; clicking a game's unique-user count opens
// the per-user breakdown from GET /dashboard/gamelist/{gameref}/{days}.
import React from "react";
import {
  Box,
  Button,
  Dialog,
  IconButton,
  Snackbar,
  Alert,
  Typography,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import FileDownloadRoundedIcon from "@mui/icons-material/FileDownloadRounded";
import PageHeader from "../../components/PageHeader";
import SectionCard from "../../components/SectionCard";
import DataTable from "../../components/DataTable";
import DashboardService from "../../Utility/dashboardService";
import { tokens } from "../../theme";
import { ProfitChip, RangeButtons, StateLine, errText, fixed, mono } from "./shared";

const BUTTONS = ["Today", "Yesterday", "Last Week", "Last Month", "Top 10", "Top 20"];

// Button index → the value the API expects. 0/1 are day offsets, 2/3 map to
// week/month, and the two top-lists ask for a row count instead.
const DAYS_FOR = { 0: 0, 1: 1, 2: 7, 3: 30 };
const TOP_COUNT_FOR = { 4: 10, 5: 20 };

const endpointFor = (index) =>
  index <= 3
    ? `/dashboard/gametransaction/${DAYS_FOR[index]}`
    : `/dashboard/topgamelist/2/${TOP_COUNT_FOR[index]}`;

export default function CasinoGames() {
  const [rows, setRows] = React.useState([]);
  const [activeIndex, setActiveIndex] = React.useState(0);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [exporting, setExporting] = React.useState(false);
  const [snack, setSnack] = React.useState(null);

  const [detailOpen, setDetailOpen] = React.useState(false);
  const [detailRows, setDetailRows] = React.useState([]);
  const [detailLoading, setDetailLoading] = React.useState(false);
  const [detailTitle, setDetailTitle] = React.useState("");

  const fetchData = React.useCallback(async (index) => {
    setLoading(true);
    setError(null);
    setActiveIndex(index);
    setRows([]);
    try {
      const result = await DashboardService.getData(endpointFor(index));
      setRows(Array.isArray(result?.data) ? result.data : []);
    } catch (e) {
      setError(errText(e));
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    fetchData(0);
  }, [fetchData]);

  // The per-game drill-down reuses the day bucket the list was loaded with.
  const openGameDetails = async (gameref, gameName) => {
    setDetailOpen(true);
    setDetailRows([]);
    setDetailTitle(gameName || "");
    setDetailLoading(true);
    try {
      const days = DAYS_FOR[activeIndex] ?? activeIndex;
      const result = await DashboardService.getData(`/dashboard/gamelist/${gameref}/${days}`);
      setDetailRows(Array.isArray(result?.data) ? result.data : []);
    } catch (e) {
      setSnack({ text: errText(e), sev: "error" });
    } finally {
      setDetailLoading(false);
    }
  };

  // The export endpoint is /gametransaction/download/{days}/{code} — it takes the
  // same day bucket as the list, not the button index, so "Last Week"/"Last Month"
  // have to send 7/30 here. The top-list views have no export of their own; the
  // button is disabled for them rather than exporting some unrelated period.
  const exportCsv = async () => {
    const days = DAYS_FOR[activeIndex];
    if (days === undefined) return;
    setExporting(true);
    try {
      await DashboardService.downloadFile(
        `/gametransaction/download/${days}/csv`,
        "gametransaction.csv"
      );
      setSnack({ text: "Export downloaded.", sev: "success" });
    } catch (e) {
      setSnack({ text: errText(e), sev: "error" });
    } finally {
      setExporting(false);
    }
  };

  const tableRows = React.useMemo(
    () =>
      rows.map((g) => ({
        name: g.gameName,
        provider: g.provider,
        gameid: g.gameid,
        gameref: g.gameref,
        unique_users: g.unique_users,
        payBets: `$${fixed(g.betamount)}`,
        winBets: `$${fixed(g.winamount)}`,
        profit: fixed(Number(g.betamount) - Number(g.winamount)),
      })),
    [rows]
  );

  const columns = [
    { header: "Game Name", accessor: "name", align: "left" },
    { header: "Provider", accessor: "provider", align: "left" },
    { header: "Game Id", accessor: "gameid", align: "left" },
    {
      header: "Unique Users",
      accessor: "unique_users",
      align: "left",
      render: (value, row) => (
        <Box
          component="span"
          onClick={() => openGameDetails(row.gameref, row.name)}
          sx={{
            cursor: "pointer",
            color: tokens.blue,
            fontFamily: mono,
            fontWeight: 600,
            "&:hover": { textDecoration: "underline" },
          }}
        >
          {value}
        </Box>
      ),
    },
    { header: "Pay Bets", accessor: "payBets", align: "center" },
    { header: "Win Bets", accessor: "winBets", align: "center" },
    {
      header: "Profit/Loss",
      accessor: "profit",
      align: "center",
      render: (v) => <ProfitChip value={v} />,
    },
  ];

  const detailTableRows = detailRows.map((d) => ({
    user_id: d.user_id,
    payBets: `$${fixed(d.betamount)}`,
    winBets: `$${fixed(d.winamount)}`,
  }));

  const detailColumns = [
    { header: "User Id", accessor: "user_id", align: "left" },
    { header: "Pay Bets", accessor: "payBets", align: "center" },
    { header: "Win Bets", accessor: "winBets", align: "center" },
  ];

  return (
    <Box>
      <PageHeader
        title="Games"
        crumb="Casino Dashboard / Games"
        subtitle="Wagering and payout per game, with the unique players behind each row"
        actions={
          <Button
            size="small"
            variant="contained"
            startIcon={<FileDownloadRoundedIcon />}
            onClick={exportCsv}
            disabled={exporting || DAYS_FOR[activeIndex] === undefined}
            title={
              DAYS_FOR[activeIndex] === undefined
                ? "Export is available for the period views only"
                : undefined
            }
          >
            {exporting ? "Exporting…" : "Export CSV"}
          </Button>
        }
      />

      <Box sx={{ mb: 1.75 }}>
        <RangeButtons
          labels={BUTTONS}
          active={activeIndex}
          onChange={fetchData}
          disabled={loading}
        />
      </Box>

      <SectionCard title="Games" sx={{ p: 2, height: "auto" }}>
        <StateLine error={error} />
        {!error && (
          <DataTable
            columns={columns}
            rows={tableRows}
            loading={loading}
            searchable
            sortable
            paginated
            entriesPerPage
            showTotalEntries
            searchPlaceholder="Search game, provider, id…"
            emptyText="No game activity for this period."
            rowKey={(r, i) => `${r.gameref}-${i}`}
          />
        )}
      </SectionCard>

      <Dialog
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { background: tokens.cardBg, border: `1px solid ${tokens.border}`, borderRadius: "12px" },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 2.5,
            py: 1.5,
            borderBottom: `1px solid ${tokens.border}`,
          }}
        >
          <Typography variant="h6" sx={{ fontSize: 16 }}>
            {detailTitle || "Game detail"}
          </Typography>
          <IconButton size="small" onClick={() => setDetailOpen(false)} aria-label="close">
            <CloseRoundedIcon fontSize="small" />
          </IconButton>
        </Box>
        <Box sx={{ p: 2 }}>
          <DataTable
            columns={detailColumns}
            rows={detailTableRows}
            loading={detailLoading}
            paginated
            emptyText="No players for this game in the selected period."
            rowKey={(r, i) => `${r.user_id}-${i}`}
          />
        </Box>
      </Dialog>

      <Snackbar
        open={!!snack}
        autoHideDuration={4000}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert severity={snack?.sev || "success"} onClose={() => setSnack(null)} variant="filled">
          {snack?.text}
        </Alert>
      </Snackbar>
    </Box>
  );
}
